import { useCallback, useContext, useEffect, useRef } from 'react';
import { type BrandInsightsPageContext, ChatContextType } from '@trinity/chat';
import { ChatPageContextDataContext } from '@trinity/chat-components/components/Chat/context';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { resetContextSubject } from '@trinity/chat-components/components/Chat.State/state.chat-session';
import { useLocation } from '@trinity/chat-components/containers/Chat/RoutingProvider';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import type { ChatSidePanelRouteAdapter } from '../side-panel-route-policy';

type ResetRule = {
  from: ChatContextType;
  to: ChatContextType;
  /** When true, only resets if the entity identifier also changes (same-type transitions). */
  requiresEntityChange?: boolean;
};

type PrevLocationState = {
  pathname: string;
  searchParams: Record<string, string>;
};

/**
 * Defines which context-level transitions should trigger a new chat.
 * Add entries here to extend reset behaviour — no other code changes needed.
 */
const CONTEXT_RESET_RULES: ResetRule[] = [
  { from: ChatContextType.Brand, to: ChatContextType.Project },
  { from: ChatContextType.Brand, to: ChatContextType.Folder },
  { from: ChatContextType.Brand, to: ChatContextType.Transcript },
  { from: ChatContextType.Project, to: ChatContextType.Folder },
  { from: ChatContextType.Project, to: ChatContextType.Transcript },
  { from: ChatContextType.Project, to: ChatContextType.Document },
  { from: ChatContextType.Folder, to: ChatContextType.Transcript },
  { from: ChatContextType.Folder, to: ChatContextType.Document },
  // Same-type transitions: reset only when navigating to a different entity
  { from: ChatContextType.Transcript, to: ChatContextType.Transcript, requiresEntityChange: true },
  { from: ChatContextType.Document, to: ChatContextType.Document, requiresEntityChange: true },
];

type PrevContext = {
  type: ChatContextType;
  entityId: string | number | null;
};

/** Parses URL search parameters into a record for comparison. */
function parseSearchParams(search: string): Record<string, string> {
  const params: Record<string, string> = {};
  const searchParams = new URLSearchParams(search);
  searchParams.forEach((value, key) => {
    params[key] = value;
  });
  return params;
}

/** Detects if a location change is a tab-only navigation (same entity, different tab). */
function isTabOnlyNavigation(
  prev: PrevLocationState | null,
  next: PrevLocationState,
  getNavigationBasePath: ChatSidePanelRouteAdapter['getNavigationBasePath']
): boolean {
  if (!prev) return false;

  // If pathname hasn't changed at all, no navigation occurred (other deps changed)
  if (prev.pathname === next.pathname) return true;

  // Treat tab changes within the same entity (the app decides which paths share a base) or document
  // as a tab-only navigation, even when the route includes a trailing parameter such as a project
  // stage, chart-library, or tag ID.
  const extractBasePath = (pathname: string): string => {
    const normalized = pathname.split('?')[0];
    const appBase = getNavigationBasePath(normalized);
    if (appBase) {
      return appBase;
    }

    const parts = normalized.split('/').filter(Boolean);
    const lastPart = parts.length > 0 ? parts[parts.length - 1] : undefined;

    if (
      parts.length >= 2 &&
      lastPart !== undefined &&
      ['preview', 'summary', 'details', 'references'].includes(lastPart)
    ) {
      return `/${parts.slice(0, -1).join('/')}`;
    }

    return normalized;
  };

  const prevBase = extractBasePath(prev.pathname);
  const nextBase = extractBasePath(next.pathname);

  // Tab-only navigation: same base path but different tab
  return prevBase === nextBase && prev.pathname !== next.pathname;
}

/**
 * Watches the current page context and dispatches `new-chat-started` whenever the user
 * navigates across a context level defined in CONTEXT_RESET_RULES (PRD AC4).
 *
 * Skips reset for tab-only navigation (same page, different tab) to preserve chat history.
 *
 * Must be called from inside the `ChatStateContainer` provider tree so that
 * `ChatStateDispatchContext` is available, and under a `ChatRoutingProvider`.
 *
 * `getNavigationBasePath` is app-specific route knowledge; pass a stable reference (a module-level
 * function), as it is a dependency of the effect below.
 */
export function useContextResetOnNavigation({
  getNavigationBasePath,
}: Pick<ChatSidePanelRouteAdapter, 'getNavigationBasePath'>): void {
  const pageContext = useContext(ChatPageContextDataContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const brandIdentifier = useActiveBrandIdentifier();
  const { pathname, search } = useLocation();
  const prevContextRef = useRef<PrevContext | null>(null);
  const prevLocationRef = useRef<PrevLocationState | null>(null);

  const resetChat = useCallback(
    (context: BrandInsightsPageContext.Item) => {
      if (!brandIdentifier) return;

      const resetLandingCards =
        context.type === ChatContextType.Agent && context.data.showLandingCardsOnNewChat;

      dispatch({
        type: 'new-chat-started',
        payload: {
          context: resetContextSubject(context, resetLandingCards),
          brandIdentifier,
          resetLandingCards,
        },
      });
    },
    [brandIdentifier, dispatch]
  );

  useEffect(() => {
    const nextLocation: PrevLocationState = {
      pathname,
      searchParams: parseSearchParams(search),
    };
    const prevLocation = prevLocationRef.current;

    if (!pageContext) {
      return;
    }

    const nextType = pageContext.type;
    const nextEntityId = 'data' in pageContext ? getEntityId(pageContext) : null;
    const prev = prevContextRef.current;

    // Skip reset if this is a tab-only navigation (same page, different tab/query param)
    if (isTabOnlyNavigation(prevLocation, nextLocation, getNavigationBasePath)) {
      return;
    }

    if (prev !== null) {
      const matchedRule = CONTEXT_RESET_RULES.find((rule) => {
        if (rule.from !== prev.type || rule.to !== nextType) return false;
        if (rule.requiresEntityChange) return nextEntityId !== prev.entityId;
        return true;
      });

      if (matchedRule) {
        // Guard before advancing the ref: if brandIdentifier is not yet loaded,
        // bail without updating prevContextRef so the reset is retried once
        // brandIdentifier becomes available (causes resetChat to change identity).
        if (!brandIdentifier) return;
        resetChat(pageContext);
      }
    }

    // Advance route and context refs only when page context is available, so transient
    // null context states during navigation do not mask real entity transitions.
    prevLocationRef.current = nextLocation;
    prevContextRef.current = { type: nextType, entityId: nextEntityId };
  }, [pageContext, resetChat, pathname, search, brandIdentifier, getNavigationBasePath]);
}

/** Extracts the primary entity identifier used to detect same-page-type navigation. */
function getEntityId(context: BrandInsightsPageContext.Item): string | number | null {
  switch (context.type) {
    case ChatContextType.Transcript:
      return context.data.transcriptId;
    case ChatContextType.Document:
      return context.data.documentId;
    case ChatContextType.Folder:
      return context.data.folderId;
    case ChatContextType.Project:
      return context.data.projectId;
    case ChatContextType.Brand:
      return context.data.brandId;
    case ChatContextType.Agent:
      return context.data.identifier;
    default:
      return null;
  }
}
